import { jsx, jsxs } from "react/jsx-runtime";
import Color from "color";
import classnames from "classnames";
import withHover from "../../helperComponents/withHover";
import getAnnotationNameAndStartStopString from "../../utils/getAnnotationNameAndStartStopString";
import React, { useState } from "react";
import { doesLabelFitInAnnotation, getAnnotationTextWidth } from "../utils";
import { noop } from "lodash-es";
import getAnnotationClassnames from "../../utils/getAnnotationClassnames";
import { ANNOTATION_LABEL_FONT_WIDTH } from "../constants";
import {
  getStripedPattern,
  getPrimerGradient,
  getSanitizedId
} from "../../utils/editorUtils";
import { partOverhangs } from "../partOverhangs";
import { Tooltip } from "@blueprintjs/core";
import {
  featureDragStore,
  startFeatureDrag
} from "./featureDragUtils";
function getAnnotationTextOffset({
  width,
  nameToDisplay,
  hasAPoint,
  pointiness,
  forward
}) {
  return width / 2 - getAnnotationTextWidth(nameToDisplay) / 2 - (hasAPoint ? (pointiness / 2 + ANNOTATION_LABEL_FONT_WIDTH / 2) * (forward ? 1 : -1) : 0);
}
function getAnnotationNameInfo({
  name,
  width,
  hasAPoint,
  pointiness,
  forward,
  charWidth,
  truncateLabelsThatDoNotFit,
  onlyShowLabelsThatDoNotFit,
  annotation
}) {
  let nameToDisplay = name;
  let textOffset = getAnnotationTextOffset({
    width,
    nameToDisplay,
    hasAPoint,
    pointiness,
    forward
  });
  const widthAvailableForText = width - ANNOTATION_LABEL_FONT_WIDTH * 2;
  if (!doesLabelFitInAnnotation(name, { width }, charWidth) || !onlyShowLabelsThatDoNotFit && ["parts", "features"].includes(annotation.annotationTypePlural)) {
    if (truncateLabelsThatDoNotFit) {
      let left = 0;
      let right = name.length;
      let bestFit = "";
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const candidate = name.slice(0, mid);
        const candidateWidth = getAnnotationTextWidth(candidate);
        if (candidateWidth <= widthAvailableForText) {
          if (candidate.length > bestFit.length) {
            bestFit = candidate;
          }
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      if (bestFit.length < name.length) {
        bestFit = bestFit.slice(0, -2) + "..";
      }
      nameToDisplay = bestFit;
      if (nameToDisplay.length <= 3) {
        textOffset = 0;
        nameToDisplay = "";
      } else {
        textOffset = getAnnotationTextOffset({
          width,
          nameToDisplay,
          hasAPoint,
          pointiness,
          forward
        });
      }
    } else {
      textOffset = 0;
      nameToDisplay = "";
    }
  }
  return { textOffset, nameToDisplay };
}
function PointedAnnotation(props) {
  const {
    className,
    widthInBps,
    charWidth,
    height,
    rangeType,
    forward,
    arrowheadType,
    name: _name = "",
    customName,
    type,
    readOnly,
    isStriped,
    onMouseLeave,
    onMouseOver,
    isProtein,
    id,
    extraHeight,
    flipAnnotation,
    insertPaths,
    insertTicks,
    hideName,
    color: propsColor,
    fill,
    stroke,
    opacity,
    onClick = noop,
    onDoubleClick = noop,
    textColor,
    onRightClick = noop,
    gapsInside,
    gapsBefore,
    annotation,
    annotationRange,
    hovered,
    sequenceLength,
    upsertFeature,
    truncateLabelsThatDoNotFit,
    onlyShowLabelsThatDoNotFit
  } = props;
  let color = propsColor || "orange";
  const isPrimer = !!annotation.primerId || ["primer", "primer_bind"].includes(String(annotation.type || "").toLowerCase()) || ["primer", "primer_bind"].includes(String(type || "").toLowerCase());
  if (isPrimer) {
    color = forward ? "#006600" : "#c2ffc2";
  }
  const name = customName || _name;
  let pointiness = props.pointiness || 4;
  let arrowPointiness = props.arrowPointiness || 1;
  const [isOpen, setOpen] = useState(false);
  const [isOpen2, setOpen2] = useState(false);
  if (arrowheadType === "NONE") {
    pointiness = 0;
    arrowPointiness = 0;
  }
  const _rangeType = annotation.rangeTypeOverride || rangeType;
  const classNames = getAnnotationClassnames(annotation, {
    isProtein,
    type,
    viewName: "RowView"
  });
  let partOverhangStart;
  let partOverhangEnd;
  partOverhangs.forEach((h) => {
    const overhangBps = props[h];
    if (overhangBps) {
      if (h.includes("fivePrime")) {
        partOverhangStart = overhangBps;
        if (h.includes("Underhang")) {
          partOverhangStart += " Underhang";
        } else if (h.includes("Overhang")) {
          partOverhangStart += " Overhang";
        }
      } else {
        partOverhangEnd = overhangBps;
        if (h.includes("Underhang")) {
          partOverhangEnd += " Underhang";
        } else if (h.includes("Overhang")) {
          partOverhangEnd += " Overhang";
        }
      }
    }
  });
  const width = (widthInBps + gapsInside) * charWidth;
  let charWN = charWidth;
  if (charWidth < 15) {
    if (width > 15) {
      charWN = 15;
    } else {
      charWN = width;
    }
  }
  charWN = arrowPointiness * charWN;
  const widthMinusOne = width - charWN;
  let path;
  let hasAPoint = false;
  const endLine = annotation.overlapsSelf ? `L 0,${height / 2}
  L -10,${height / 2}
  L 0,${height / 2} ` : "";
  const bottomLine = `${insertTicks || ""} L 0,${height}`;
  const startLines = `M 0,0
  ${insertPaths || ""}`;
  const arrowLine = annotation.overlapsSelf ? `L ${width + 10},${height / 2}
  L ${width},${height / 2} ` : "";
  if (_rangeType === "middle") {
    path = `
        ${startLines}
        L ${width - pointiness / 2},0
        Q ${width + pointiness / 2},${height / 2} ${width - pointiness / 2},${height}
        ${bottomLine}
        Q ${pointiness},${height / 2} ${0},${0}
        z`;
  } else if (_rangeType === "start") {
    path = `
        ${startLines}
        L ${width - pointiness / 2},0

        Q ${width + pointiness / 2},${height / 2} ${width - pointiness / 2},${height}

        ${bottomLine}
        ${endLine}
        z`;
  } else if (_rangeType === "beginningAndEnd") {
    hasAPoint = true;
    path = `
        ${startLines}
        L ${widthMinusOne},0
        L ${width},${height / 2}
        ${arrowLine}
        L ${widthMinusOne},${height}
        ${bottomLine}
        ${endLine}
        z`;
  } else {
    hasAPoint = true;
    path = `
      ${startLines}
      L ${widthMinusOne},0
      L ${width},${height / 2}
      ${arrowLine}
      L ${widthMinusOne},${height}
      ${bottomLine}
      Q ${pointiness},${height / 2} ${0},${0}
      z`;
  }
  const { textOffset, nameToDisplay } = getAnnotationNameInfo({
    name,
    width,
    hasAPoint,
    pointiness,
    forward,
    charWidth,
    truncateLabelsThatDoNotFit,
    onlyShowLabelsThatDoNotFit,
    annotation
  });
  let _textColor = textColor;
  if (!textColor) {
    if (isPrimer) {
      _textColor = "black";
    } else {
      try {
        _textColor = Color(color).isDark() ? "white" : "black";
      } catch (error) {
        _textColor = "white";
      }
    }
  }
  const isFeature = !readOnly && (type === "feature" || annotation?.annotationTypePlural === "features");
  const isCurrentlyDragging = featureDragStore.draggingFeatureId === (annotation?.id || id);
  const showHandles = isFeature && (hovered || isCurrentlyDragging);
  const hasStartHandle = showHandles && annotationRange && annotation && annotationRange.start === annotation.start;
  const hasEndHandle = showHandles && annotationRange && annotation && annotationRange.end === annotation.end;
  const handleSize = 8;
  const handleHitSize = 16;
  return /* @__PURE__ */ jsxs(
    "g",
    {
      ...{ onMouseLeave, onMouseOver },
      className: ` clickable ${className} ${classNames} ${forward ? "ann-forward" : `ann-reverse`}`,
      "data-id": id,
      style: { opacity },
      onClick: function(event) {
        onClick({ annotation, event, gapsBefore, gapsInside });
      },
      onDoubleClick: function(event) {
        onDoubleClick && onDoubleClick({ annotation, event, gapsBefore, gapsInside });
      },
      onContextMenu: function(event) {
        onRightClick({ annotation, event, gapsBefore, gapsInside });
      },
      children: [
        !(isOpen || isOpen2) && /* @__PURE__ */ jsx("title", { children: getAnnotationNameAndStartStopString(annotation, {
          isProtein,
          readOnly
        }) }),
        isStriped && getStripedPattern({ color, id }),
        isPrimer && getPrimerGradient({ id, forward }),
        /* @__PURE__ */ jsx(
          "path",
          {
            strokeWidth: "1",
            stroke: stroke || "black",
            fill: isPrimer ? `url(#primerGradient-${getSanitizedId(id)})` : fill || color,
            transform: forward ? null : "translate(" + width + `,${flipAnnotation ? -extraHeight + 10 : 0}) scale(-1,${flipAnnotation ? "-" : ""}1) `,
            d: path
          }
        ),
        isStriped && /* @__PURE__ */ jsx(
          "path",
          {
            strokeWidth: "1",
            fill: `url(#diagonalHatch-${getSanitizedId(id)})`,
            transform: forward ? null : "translate(" + width + `,${flipAnnotation ? -extraHeight + 10 : 0}) scale(-1,${flipAnnotation ? "-" : ""}1) `,
            d: path
          }
        ),
        partOverhangStart && (rangeType === (forward ? "start" : "end") || rangeType === "beginningAndEnd") && /* @__PURE__ */ jsx(
          Tooltip,
          {
            onInteraction: (isOpen3) => {
              setOpen(isOpen3);
            },
            isOpen,
            wrapperTagName: "g",
            targetTagName: "g",
            content: partOverhangStart,
            children: /* @__PURE__ */ jsx(
              "circle",
              {
                className: "partWithOverhangsIndicator",
                style: { fillOpacity: 1 },
                fill: "#ac68cc",
                cx: (forward ? -7 : 0) + 15,
                cy: 7,
                r: 4
              }
            )
          }
        ),
        partOverhangEnd && (rangeType === (forward ? "end" : "start") || rangeType === "beginningAndEnd") && /* @__PURE__ */ jsx(
          Tooltip,
          {
            onInteraction: (isOpen3) => {
              setOpen2(isOpen3);
            },
            isOpen: isOpen2,
            wrapperTagName: "g",
            targetTagName: "g",
            content: partOverhangEnd,
            children: /* @__PURE__ */ jsx(
              "circle",
              {
                className: "partWithOverhangsIndicator",
                style: { fillOpacity: 1 },
                fill: "#ac68cc",
                cx: (forward ? -7 : 0) + width - 10,
                cy: 7,
                r: 4
              }
            )
          }
        ),
        !hideName && nameToDisplay && /* @__PURE__ */ jsx(
          "text",
          {
            className: classnames(
              "veLabelText ve-monospace-font",
              annotation.labelClassName
            ),
            style: {
              // fontSize: ".8em",
              fill: _textColor
            },
            transform: `translate(${textOffset},${height - (isPrimer ? 6 : 2)})`,
            children: nameToDisplay
          }
        ),
        hasStartHandle && /* @__PURE__ */ jsxs(
          "g",
          {
            className: "veFeatureDragHandleGroup veFeatureDragStartGroup",
            style: { cursor: "ew-resize", pointerEvents: "all" },
            onMouseDown: (e) => {
              startFeatureDrag({
                event: e,
                handleType: "start",
                annotation,
                charWidth,
                sequenceLength,
                isProtein,
                upsertFeature
              });
            },
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            onDoubleClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            onContextMenu: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            children: [
              /* @__PURE__ */ jsx("title", { children: "Drag to change feature start" }),
              /* @__PURE__ */ jsx(
                "rect",
                {
                  x: -handleHitSize / 2,
                  y: (height - handleHitSize) / 2,
                  width: handleHitSize,
                  height: handleHitSize,
                  fill: "transparent",
                  style: { cursor: "ew-resize" }
                }
              ),
              /* @__PURE__ */ jsx(
                "rect",
                {
                  className: "veFeatureDragHandle veFeatureDragStart",
                  x: -handleSize / 2,
                  y: (height - handleSize) / 2,
                  width: handleSize,
                  height: handleSize,
                  fill: "#ffffff",
                  stroke: "#222222",
                  strokeWidth: "1.2",
                  rx: "1",
                  ry: "1",
                  style: { cursor: "ew-resize" }
                }
              )
            ]
          }
        ),
        hasEndHandle && /* @__PURE__ */ jsxs(
          "g",
          {
            className: "veFeatureDragHandleGroup veFeatureDragEndGroup",
            style: { cursor: "ew-resize", pointerEvents: "all" },
            onMouseDown: (e) => {
              startFeatureDrag({
                event: e,
                handleType: "end",
                annotation,
                charWidth,
                sequenceLength,
                isProtein,
                upsertFeature
              });
            },
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            onDoubleClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            onContextMenu: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            children: [
              /* @__PURE__ */ jsx("title", { children: "Drag to change feature end" }),
              /* @__PURE__ */ jsx(
                "rect",
                {
                  x: width - handleHitSize / 2,
                  y: (height - handleHitSize) / 2,
                  width: handleHitSize,
                  height: handleHitSize,
                  fill: "transparent",
                  style: { cursor: "ew-resize" }
                }
              ),
              /* @__PURE__ */ jsx(
                "rect",
                {
                  className: "veFeatureDragHandle veFeatureDragEnd",
                  x: width - handleSize / 2,
                  y: (height - handleSize) / 2,
                  width: handleSize,
                  height: handleSize,
                  fill: "#ffffff",
                  stroke: "#222222",
                  strokeWidth: "1.2",
                  rx: "1",
                  ry: "1",
                  style: { cursor: "ew-resize" }
                }
              )
            ]
          }
        )
      ]
    }
  );
}
export default withHover(PointedAnnotation);
